import { ThemeProvider, ThemeProviderProps } from "../ThemeProvider";
import themeStyles from "../ThemeProvider/ThemeProvider.module.scss";
import {
  ComponentPropsWithoutRef,
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import cx from "classnames";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";
import { ITranslations } from "../LocalizationProvider/LocalizationProvider";
import {
  useBreakpoint,
  type BreakpointReturnProps,
} from "../../hooks/useBreakpoint";
import { useTranslations } from "../LocalizationProvider/useTranslations";
import { LocalizationContext } from "../LocalizationProvider";

export type HammerProviderProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    children?: ReactNode;

    themeData?: {
      theme?: ThemeProviderProps["theme"];
      mode?: ThemeProviderProps["mode"];
    };

    localizationData?: {
      locale?: ITranslations["locale"];
      dir?: ITranslations["dir"];
      translations?: ITranslations["translations"];
    };
  };

export type HammerProviderContextProps = {
  breakpoint?: BreakpointReturnProps;
};

export const HammerProviderContext = createContext<HammerProviderContextProps>(
  {},
);

export const TooltipLegacyGroupContext = createContext<{
  disableDelayGroup: Dispatch<SetStateAction<boolean>>;
} | null>(null);

export const useTooltipLegacyGroup = () => {
  const context = useContext(TooltipLegacyGroupContext);

  if (context == null) {
    throw new Error(
      "Tooltip components must be wrapped in <HammerProvider /> or <TooltipGroupContext.Provider />",
    );
  }

  return context;
};

export const HammerProvider = (props: HammerProviderProps) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);

  const { themeData, localizationData, children, className, style, ...rest } =
    componentProps;
  const {
    locale = "en",
    dir = "ltr",
    translations = {},
  } = localizationData ?? {};

  const classNameCombined = cx(className, [themeStyles["theme-core"]], {
    [themeStyles["mode-dark"]]: themeData?.mode === "dark",
    [themeStyles["mode-light"]]: themeData?.mode === "light",
  });
  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  const breakpoint = useBreakpoint();

  const [disableDelayGroup, setDisableDelayGroup] = useState(false);

  const { translations: t } = useTranslations(translations);

  const localizationContextValue = useMemo(() => ({ locale, t }), [locale, t]);
  const tooltipLegacyGroupContextValue = useMemo(
    () => ({ disableDelayGroup: setDisableDelayGroup }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableDelayGroup],
  );

  return (
    <HammerProviderContext.Provider value={{ breakpoint }}>
      <ThemeProvider
        className={classNameCombined}
        style={styleCombined}
        theme={themeData?.theme}
        mode={themeData?.mode}
        dir={dir}
        {...rest}
      >
        <TooltipLegacyGroupContext.Provider
          value={tooltipLegacyGroupContextValue}
        >
          <LocalizationContext.Provider value={localizationContextValue}>
            {children}
          </LocalizationContext.Provider>
        </TooltipLegacyGroupContext.Provider>
      </ThemeProvider>
    </HammerProviderContext.Provider>
  );
};
